<template>
  <el-row class="sp-options-panel h-full">
    <el-input
      v-model="keywords"
      clearable
      size="medium"
      placeholder="输入关键词搜索"
      @click.native.stop
      @input="onKeywords"
    />
    <el-col class="option-panel available-panel" :span="12">
      <div class="header font-size-16" @click.stop>可选列表</div>
      <div class="content" @click.stop>
        <ul
          v-show="allOptions.length"
          class="p-0 h-full w-full list-none select-list available"
        >
          <el-scrollbar class="h-full" wrap-style="overflow-x: hidden;">
            <li
              v-for="(availableOption, index) in allOptions"
              :key="index"
              :title="availableOption.label"
              class="w-full flex justify-between items-center select-item"
              :class="{
                'is-selected': hasSelectedOption(availableOption.value),
                'is-disabled': selectedValue.length >= multipleLimit && !hasSelectedOption(availableOption.value),
              }"
              @click.stop="toggleOption(availableOption.value)"
            >
              <span class="flex-1 truncate">{{ availableOption.label }}</span>
              <i
                v-show="hasSelectedOption(availableOption.value)"
                class="el-icon-check font-size-16"
              />
            </li>
          </el-scrollbar>
        </ul>
        <el-empty
          v-show="allOptions.length === 0"
          :image-size="75"
          description="暂无数据"
        />
      </div>
    </el-col>
    <el-col class="option-panel selected-panel h-full" :span="12">
      <div class="header w-full flex items-center justify-between" @click.stop>
        <span class="font-size-16">{{ selectedValue.length }}/{{ backupOptions.length }}项</span>
        <el-button type="text" :disabled="selectedValue.length === 0" @click="clearOptions">一键清空</el-button>
      </div>
      <div class="content" @click.stop>
        <ul
          v-show="selectedValue.length"
          class="p-0 list-none h-full w-full select-list"
        >
          <el-scrollbar class="h-full" wrap-style="overflow-x: hidden;">
            <li
              v-for="selectedOption in selectedOptions"
              :key="selectedOption.value"
              :title="selectedOption.label"
              class="select-item w-full flex justify-between items-center"
            >
              <span class="flex-1 truncate">{{ selectedOption.label }}</span>
              <i
                class="el-icon-close font-size-16 icon-close"
                @click="removeSelectedOption(selectedOption.value)"
              />
            </li>
          </el-scrollbar>
        </ul>
        <el-empty
          v-show="selectedValue.length === 0"
          :image-size="75"
          description="暂无数据"
        />
      </div>
    </el-col>
  </el-row>
</template>

<script>
import { deepClone, serialize } from './utils';

export default {
  name: 'SpOptionsPanel',
  props: {
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    data: {
      type: Array,
      default() {
        return [];
      },
    },
    multipleLimit: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      backupOptions: [],
      allOptions: [],
      selectedValue: [],
      keywords: '',
    };
  },
  computed: {
    selectedOptions() {
      return this.backupOptions.filter((option) => this.selectedValue.map(val => serialize(val)).includes(serialize(option.value)));
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(val) {
        this.selectedValue = deepClone(val);
      },
    },
    data: {
      deep: true,
      immediate: true,
      handler(val) {
        const data = deepClone(val);
        this.backupOptions = Object.freeze(data);
        this.allOptions = data;
      },
    },
  },

  methods: {
    /**
     * 派发改变事件
     * @returns {void}
     */
    dispatchChangeEvent() {
      this.$emit('change', deepClone(this.selectedValue));
    },
    /**
     * 已选列表中是否存在指定选项
     * @param {Object} selectedOption 指定选项对象
     * @returns {boolean}
     */
    hasSelectedOption(selectedOption) {
      const is_equal = (option) => serialize(option) === serialize(selectedOption);
      return this.selectedValue.some(is_equal);
    },
    /**
     * 获取选项在选项列表中所在的索引位置
     * @param {Array} options 选项列表
     * @param {Object} option 选项对象
     */
    getOptionIndex(options, option) {
      return options.findIndex((item) => serialize(item) === serialize(option));
    },
    /**
     * 添加选项到已选列表
     * @param {Object} selectedOption 选项
     * @returns {void}
     */
    addSelectedOption(selectedOption) {
      this.selectedValue.push(selectedOption);
      this.dispatchChangeEvent();
    },
    /**
     * 从已选列表移除选项
     * @param {Object} selectedOption 选项对象
     * @returns {void}
     */
    removeSelectedOption(selectedOption) {
      const index = this.getOptionIndex(this.selectedValue, selectedOption);
      if (index !== -1) {
        this.selectedValue.splice(index, 1);
        this.dispatchChangeEvent();
      }
    },
    /**
     * 切换选项选中状态
     * @param {Object} option 选项对象
     * @returns {void}
     */
    toggleOption(option) {
      const isExistOption = this.hasSelectedOption(option);
      const isOverflow = this.selectedValue.length >= this.multipleLimit;
      if (isOverflow && !isExistOption) {
        return;
      }
      isExistOption ? this.removeSelectedOption(option) : this.addSelectedOption(option);
    },
    /**
     * 清空已选项
     * @returns {void}
     */
    clearOptions() {
      this.selectedValue.splice(0);
      this.dispatchChangeEvent();
    },
    /**
     * 关键词搜索
     * @param {string} keywords 关键词
     * @returns {void}
     */
    onKeywords(keywords) {
      const options = this.backupOptions.filter((option) => {
        return option.label.indexOf(keywords) !== -1;
      });
      this.allOptions = options;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './style/index.scss';
.sp-options-panel {
  cursor: pointer;
  .option-panel {
    &.available-panel {
      border-right: 1px solid #dcdfe6;
    }
    .header {
      height: 40px;
      line-height: 40px;
      background: #f5f7fa;
      padding: 0 16px;
      box-sizing: border-box;
      color: #000;
    }
    .content {
      background-color: #ffffff;
      height: 204px;
      .select-item {
        padding: 0 16px;
        box-sizing: border-box;
        line-height: 34px;
        font-size: 14px;
        cursor: pointer;
        color: #606266;
        &:hover {
          background-color: #f5f7fa;
        }
        &.is-selected {
          color: #409eff;
          font-weight: 700;
        }
        &.is-disabled {
          color: #c0c4cc;
          cursor: not-allowed;
        }
        .icon-close {
          border-radius: 50%;
          text-align: center;
          height: 20px;
          width: 20px;
          line-height: 20px;
          vertical-align: middle;
          &:hover {
            color: #fff;
            font-size: 14px;
            background-color: #909399;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.sp-select-popper-panel {
  &.el-select-dropdown {
    height: 300px;
    max-height: 300px;
    &.is-multiple {
      .el-scrollbar {
        overflow: hidden;
        .el-select-dropdown__wrap {
          max-height: 300px;
          .el-select-dropdown__item {
            padding: 0;
            height: 280px;
            height: 280px;
          }
        }
      }
    }
  }
}
</style>
